import Layout from '@/components/layout/Layout';
import { FC, useState } from 'react';
import Link from 'next/link';
import BookCardItem from '@/components/ui/book-card-item/BookCardItem';
import Pagination from '@/components/ui/pagination/Pagination';
import SectionTitle from '@/components/ui/section-title/SectionTitle';
import Breadcrumbs from '@/components/ui/breadcrumbs/Breadcrumbs';
import CardsControls from '@/components/ui/cards-controls/CardsControls';
import SimilarAuthors from '@/components/ui/library-authors/SimilarAuthors';
import { BOOKS_FOR_PAGE } from '@/helpers/variables';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { getAuthorFullName } from '@/helpers/helpers';
import { IAuthorDetail, IAuthorBookData } from '@/interfaces/authorDetail.interface';

import { ISimilarAuthors } from '@/interfaces/similarAuthors';
import { LITRES_AUTHORS } from '@/helpers/variables';
import styles from './AuthorDetail.module.scss';

interface IAuthorDetailScreen {
  books: IAuthorDetail;
  similarAuthors: ISimilarAuthors;
}

const AuthorDetail: FC<IAuthorDetailScreen> = ({ books, similarAuthors }) => {
  const isLitresAuthor = LITRES_AUTHORS.some((item) => item === books.alias);

  const pagesCount = Math.ceil(books.books.total_count / BOOKS_FOR_PAGE);

  const urlPath = useGetUrlPath();
  const getAuthorsAliasOrId = urlPath.includes(String(books.base_author_data.id)) ? `${books.base_author_data.id}` : `${books.alias}`;

  const authorData = books.base_author_data;
  const authorFullName = getAuthorFullName(authorData.first_name, authorData.middle_name, authorData.last_name);

  const [isCardsColumn, setIsCardsColumn] = useState(false);

  const SEO_TITLE = `${authorFullName} - список всех книг автора скачать или читать онлайн бесплатно`;
  const SEO_DESCRIPTION = `Фензин - электронная библиотека, где вы можете прочитать все книги автора «${authorFullName}» или скачать в fb2, epub, txt, pdf`;
  const SEO_CANONICAL = `/library/author/${getAuthorsAliasOrId}`;

  const getBookBreadcrumbs = () => {
    return {
      __html: `{
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem","position": 1,"name": "Главная","item": "https://fenzin.org"},
        {"@type": "ListItem","position": 2,"name": "Aвторы","item": "https://fenzin.org/library"},
        {"@type": "ListItem","position": 3,"name": "${authorFullName}","item": "https://fenzin.org/library/author/${authorData.id}"}
       ]
    }`,
    };
  };

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} pageHref={useGetUrlPath()}>
      <Breadcrumbs firstPageName="Все авторы" firstPageAlias="/library/" secondPageName={authorFullName} />
      <script type="application/ld+json" dangerouslySetInnerHTML={getBookBreadcrumbs()} key="product-jsonld" />
      <SectionTitle name={authorFullName} extraClassName="mainTitle_filter" />
      <div data-btn-cards={isCardsColumn ? 'tiles' : 'rows'}>
        <CardsControls setIsCardsColumn={setIsCardsColumn} extraClassName="" />
        {isLitresAuthor && (
          <h2>
            <Link prefetch={false} className={styles.litresAuthorLink} href={`https://www.litres.ru/author/${books.alias}/`} target="_blank">{`${authorData.first_name} ${authorData.last_name} книги`}</Link>
          </h2>
        )}
        {!isLitresAuthor && <h2 className={styles.h2}>Книги автора: {authorFullName}</h2>}
      </div>
      <div data-cards="list">
        <div className={styles.bookCardsList}>
          {books.books.data.length ? (
            books.books.data.map((book: IAuthorBookData) => (
              <BookCardItem type={book.type} freeVersion={book.allow_full_free > 0} id={book.satellite_book.id} author={authorData} book={book} key={book.id} rating={book.satellite_book.rating} isAuthor />
            ))
          ) : (
            <div>Книги не найдены</div>
          )}
        </div>
      </div>
      <Pagination pagesCount={pagesCount ?? 0} postName={`library/author/${books.id}/page-library=`} />

      {similarAuthors && similarAuthors.length > 0 && <SimilarAuthors similarAuthors={similarAuthors} />}
    </Layout>
  );
};

export default AuthorDetail;
